<template>
  <section class="benefits-tiers-section">
    <header class="benefits-tiers-header">
      <label class="benefits-tiers-toggle-row">
        <span>{{ t("benefitsCenter-tiers-systemLabel") }}</span>
        <BenefitsToggle :value="value.enabled" :disabled="!pointsEnabled" :aria-label="t('benefitsCenter-tiers-systemLabel')" @input="toggleSystem" />
      </label>

      <p v-if="!pointsEnabled" class="benefits-tiers-disabled-notice">
        {{ t("benefitsCenter-tiers-pointsDisabledNotice") }}
      </p>

      <p v-else-if="!value.enabled && orderedTiers.length" class="benefits-tiers-disabled-notice">
        {{ t("benefitsCenter-tiers-disabledNotice") }}
      </p>

      <hr aria-hidden="true" />

      <h2>{{ t("benefitsCenter-tiers-title") }}</h2>
    </header>

    <div v-if="orderedTiers.length" class="benefits-tiers-list">
      <KvButton class="benefits-tiers-add-button" type="button" variant="outlined" prepend-icon="ui-plus" @click="openCreatePanel">
        {{ t("benefitsCenter-tiers-addLevel") }}
      </KvButton>

      <draggable
        :value="orderedTiers"
        class="benefits-tiers-sortable"
        :options="dragOptions"
        @input="reorder"
      >
        <BenefitsTierCard
          v-for="tier in orderedTiers"
          :key="tier.id"
          :tier="tier"
          interactive
          sortable
          @click="openEditPanel"
        />
      </draggable>
    </div>

    <BenefitsEmptyState
      v-else
      :title="t('benefitsCenter-tiers-emptyTitle')"
      :description="t('benefitsCenter-tiers-emptyDescription')"
      :button-text="t('benefitsCenter-tiers-createFirst')"
      :bg-opacity="0.15"
      :halo-opacity="0.3"
      @action="openCreatePanel"
    >
      <template #background>
        <BenefitsTierCard v-for="(example, index) in exampleTiers" :key="example.id" :tier="example"
          :style="{ opacity: index === 0 ? 1 : index === 1 ? 0.5 : 0.25 }" />
      </template>
    </BenefitsEmptyState>

    <BenefitsTierPanel
      v-model="panelOpen"
      :tier="selectedTier"
      :tiers="orderedTiers"
      :rewards="rewards"
      :saving="saving"
      @save="saveTier"
      @delete="openDeleteModal"
      @close="selectedTier = null"
    />

    <BenefitsTierDeleteModal
      v-model="deleteModalOpen"
      :tier="tierToDelete"
      :loading="deleting"
      @confirm="deleteTier"
    />
  </section>
</template>

<script>
import draggable from "vuedraggable";
import BenefitsToggle from "../../shared/benefits-toggle.vue";
import BenefitsEmptyState from "../../shared/benefits-empty-state.vue";
import BenefitsTierCard from "./benefits-tier-card.vue";
import BenefitsTierPanel from "./benefits-tier-panel.vue";
import BenefitsTierDeleteModal from "./benefits-tier-delete-modal.vue";
import { sortByPosition, withPositions } from "./tiers-utils";

export default {
  name: "BenefitsTiersSection",
  components: {
    draggable,
    BenefitsToggle,
    BenefitsEmptyState,
    BenefitsTierCard,
    BenefitsTierPanel,
    BenefitsTierDeleteModal,
  },
  props: {
    value: { type: Object, default: () => ({ enabled: false, items: [] }) },
    rewards: { type: Array, default: () => [] },
    pointsEnabled: { type: Boolean, default: false },
  },
  data() {
    return {
      panelOpen: false,
      selectedTier: null,
      saving: false,
      deleteModalOpen: false,
      tierToDelete: null,
      deleting: false,
    };
  },
  computed: {
    orderedTiers() {
      return sortByPosition(this.value.items);
    },
    dragOptions() {
      return {
        animation: 180,
        easing: "cubic-bezier(0.2, 0, 0, 1)",
        handle: ".benefits-tier-card-handle",
        ghostClass: "benefits-tiers-sort-ghost",
        chosenClass: "benefits-tiers-sort-chosen",
        dragClass: "benefits-tiers-sort-drag",
      };
    },
    exampleTiers() {
      return [
        { id: "bronze", iconType: "emoji", iconEmoji: "🥉", color: "#B45F24", textColor: "#FCFCFD" },
        { id: "silver", iconType: "emoji", iconEmoji: "🥈", color: "#6C737F", textColor: "#FCFCFD" },
        { id: "gold", iconType: "emoji", iconEmoji: "🥇", color: "#F9D411", textColor: "#1F2A37" },
      ].map((example) => ({
        ...example,
        name: this.t(`benefitsCenter-tiers-examples-${example.id}-title`),
        description: this.t(`benefitsCenter-tiers-examples-${example.id}-description`),
      }));
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    toggleSystem(enabled) {
      this.$emit("toggle-system", enabled);
    },
    openCreatePanel() {
      this.selectedTier = null;
      this.panelOpen = true;
    },
    openEditPanel(tier) {
      this.selectedTier = tier;
      this.panelOpen = true;
    },
    requestFromParent(event, payload) {
      return new Promise((resolve) => this.$emit(event, payload, resolve));
    },
    async saveTier(tier) {
      this.saving = true;
      const saved = await this.requestFromParent("save-tier", tier);
      this.saving = false;

      if (saved) {
        this.panelOpen = false;
        this.selectedTier = null;
      }
    },
    openDeleteModal(tier) {
      this.tierToDelete = tier;
      this.deleteModalOpen = true;
    },
    async deleteTier(deletion) {
      this.deleting = true;
      const deleted = await this.requestFromParent("delete-tier", deletion);
      this.deleting = false;

      if (deleted) {
        this.deleteModalOpen = false;
        this.tierToDelete = null;
        this.panelOpen = false;
        this.selectedTier = null;
      }
    },
    reorder(orderedTiers) {
      this.$emit("reorder-tiers", withPositions(orderedTiers));
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-tiers-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.benefits-tiers-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  h2 {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    margin: 0;
  }

  hr {
    border: 0;
    border-top: 1px solid #e5e7eb;
    margin: 0;
    width: 100%;
  }
}

.benefits-tiers-toggle-row {
  align-items: center;
  align-self: flex-start;
  color: #384250;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  gap: 8px;
  line-height: 1.2;
  margin: 0;
}

.benefits-tiers-disabled-notice {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #4d5761;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  line-height: 1.4;
  margin: 0;
  padding: 10px 12px;
}

.benefits-tiers-list,
.benefits-tiers-sortable {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.benefits-tiers-add-button {
  font-size: 16px;
  font-weight: 400;
  min-height: 56px;
  width: 100%;
}

.benefits-tiers-sortable::v-deep .benefits-tiers-sort-ghost {
  opacity: 0.35;
}

.benefits-tiers-sortable::v-deep .benefits-tiers-sort-chosen {
  box-shadow: 0 8px 24px rgba(17, 25, 39, 0.14), 0 0 0 2px rgba(42, 170, 76, 0.4);
}

.benefits-tiers-sortable::v-deep .benefits-tiers-sort-drag {
  cursor: grabbing;
  opacity: 0.95;
}
</style>
